import React, { useEffect, useState } from 'react';

import { AttachmentImage } from '@components/Attachment';
import Button from '@components/Button';
import Checkbox from '@components/Checkbox';
import ModalBase from '@components/ModalBase';

import { isMobile } from '@utils/AppUtil';

import './style.scss';

const SUBMIT_AFTER_NONE_OF_THESE_LABEL = 'Submit My Request';
const FOLLOW_AND_SKIP_LABEL_MOBILE = 'Follow & Skip Submission';
const NONE_OF_THESE_CHECKBOX_ID = 'duplicate-detection-none-of-these';

const getFollowAndSkipLabel = (newSelectionCount: number): string => {
  if (isMobile()) {
    return FOLLOW_AND_SKIP_LABEL_MOBILE;
  }

  return newSelectionCount === 1
    ? 'Follow selected request and skip my submission'
    : 'Follow selected requests and skip my submission';
};

export interface IDuplicateDetectionModalProps {
  show: boolean;
  duplicates: ICRMDuplicateIssueResult[];
  onSubmitAnyway: () => void;
  onClose: () => void;
  onFollowDuplicate?: (duplicateId: number) => void;
  onUnfollowDuplicate?: (duplicateId: number) => void;
  onFollowAndSkip?: (duplicateIds: number[]) => void;
  onDiscard?: () => void;
  submitAnywayLabel?: string;
  subtitle?: string;
  citizenDuplicateOverrideFlow?: boolean;
}

const metersToMiles = (meters: number): string => {
  const miles = meters / 1609.34;
  if (miles < 0.1) {
    return '< 0.1 mi away';
  }

  return `${miles.toFixed(1)} mi away`;
};

const getRelativeDate = (createdAt: string): string => {
  const diffMs = Date.now() - new Date(createdAt).getTime();
  const days = Math.floor(diffMs / 86400000);
  if (days === 0) {
    return 'today';
  }
  if (days === 1) {
    return '1d ago';
  }

  return `${days}d ago`;
};

const DESCRIPTION_MAX_LENGTH = 80;

const truncateDescription = (text: string): string => {
  if (!text || text.length <= DESCRIPTION_MAX_LENGTH) {
    return text;
  }

  return `${text.slice(0, DESCRIPTION_MAX_LENGTH).trim()}...`;
};

const formatFollowerCount = (count: number): string => `${count} ${count === 1 ? 'Follower' : 'Followers'}`;

const DuplicatePlaceholderThumbnail: React.FC = () => (
  <div
    className="duplicate-card__thumbnail duplicate-card__thumbnail--placeholder"
    aria-hidden="true"
    data-testid="thumbnail-placeholder"
  >
    <img src="/assets/no-image.svg" alt="" className="duplicate-card__thumbnail-placeholder-icon" />
  </div>
);

interface IDuplicateCardProps {
  duplicate: ICRMDuplicateIssueResult;
  isStaffMode: boolean;
  isSelected?: boolean;
  isSelectable?: boolean;
  isLocked?: boolean;
  itemRole?: 'listitem' | 'option';
  onSelect?: (duplicateId: number) => void;
  onFollow?: (duplicateId: number) => void;
  onUnfollow?: (duplicateId: number) => void;
  onCitizenUnfollow?: (duplicateId: number) => void;
}

const DuplicateCard: React.FC<IDuplicateCardProps> = ({
  duplicate,
  isStaffMode,
  isSelected = false,
  isSelectable = false,
  isLocked = false,
  itemRole = 'listitem',
  onSelect,
  onFollow,
  onUnfollow,
  onCitizenUnfollow,
}) => {
  const isOption = itemRole === 'option';
  const cardClassName = [
    'duplicate-card',
    isSelectable && !isLocked ? 'duplicate-card--clickable' : '',
    isSelected ? 'duplicate-card--selected' : '',
    isLocked ? 'duplicate-card--locked' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleClick = () => {
    if (isSelectable && !isLocked && onSelect) {
      onSelect(duplicate.id);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isSelectable && !isLocked && onSelect && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onSelect(duplicate.id);
    }
  };

  return (
    <div
      className={cardClassName}
      role={itemRole}
      onClick={isSelectable && !isLocked ? handleClick : undefined}
      onKeyDown={isSelectable && !isLocked ? handleKeyDown : undefined}
      tabIndex={isSelectable && !isLocked ? 0 : undefined}
      aria-selected={isOption ? (isLocked ? false : isSelected) : undefined}
      aria-disabled={isLocked ? true : undefined}
    >
      {duplicate.thumbnailUrl ? (
        <AttachmentImage
          src={duplicate.thumbnailUrl}
          alt={`${duplicate.category} submission thumbnail`}
          className="duplicate-card__thumbnail"
        />
      ) : (
        <DuplicatePlaceholderThumbnail />
      )}
      <div className="duplicate-card__content">
        <span className="duplicate-card__category">{duplicate.category}</span>
        <p className="duplicate-card__description">{truncateDescription(duplicate.descriptionPreview)}</p>
        <div className="duplicate-card__meta">
          <span className="duplicate-card__meta-item">
            <img
              src="/assets/map-pin-outline.svg"
              alt="Map pin icon"
              className="duplicate-card__meta-icon duplicate-card__meta-icon--map-pin"
              aria-hidden="true"
            />
            {metersToMiles(duplicate.distance)}
          </span>
          <span className="duplicate-card__meta-item">
            <i className="fa fa-calendar duplicate-card__meta-icon" aria-hidden="true" />
            Reported {getRelativeDate(duplicate.createdAt)}
          </span>
        </div>
      </div>
      {isStaffMode ? (
        <div className="duplicate-card__action">
          <span className="duplicate-card__follower-count">{formatFollowerCount(duplicate.followerCount)}</span>
          {duplicate.isSubmittedByContact ? (
            <>
              <span className="duplicate-card__separator">&middot;</span>
              <span className="duplicate-card__follower-count">Reported by this customer</span>
            </>
          ) : duplicate.isFollowedByContact ? (
            <>
              <span className="duplicate-card__following-label">
                <i className="fa fa-check" aria-hidden="true" /> Following
              </span>
              <span className="duplicate-card__separator">&middot;</span>
              <button type="button" className="duplicate-card__action-btn" onClick={() => onUnfollow?.(duplicate.id)}>
                Unfollow
              </button>
            </>
          ) : (
            <>
              <span className="duplicate-card__separator">&middot;</span>
              <button type="button" className="duplicate-card__action-btn" onClick={() => onFollow?.(duplicate.id)}>
                Follow
              </button>
            </>
          )}
        </div>
      ) : duplicate.isSubmittedByContact ? (
        <span className="duplicate-card__submitted-by-you">Submitted by you</span>
      ) : duplicate.isFollowedByContact && onCitizenUnfollow ? (
        <div className="duplicate-card__action duplicate-card__citizen-followed-actions">
          <span className="duplicate-card__following-label">
            <i className="fa fa-check" aria-hidden="true" /> Following
          </span>
          <span className="duplicate-card__separator">&middot;</span>
          <button type="button" className="duplicate-card__action-btn" onClick={() => onCitizenUnfollow(duplicate.id)}>
            Unfollow
          </button>
        </div>
      ) : (
        <div className="duplicate-card__checkbox" aria-hidden="true">
          <div
            className={`duplicate-card__checkbox-box${
              isSelected ||
              (isLocked && duplicate.isFollowedByContact && !duplicate.isSubmittedByContact && !onCitizenUnfollow)
                ? ' duplicate-card__checkbox-box--checked'
                : ''
            }`}
          />
        </div>
      )}
    </div>
  );
};

const DEFAULT_SUBTITLE =
  'This request may have already been submitted. If you see your request below, please select it to continue.';

const NONE_OF_THESE_HEADING = 'None of these requests are the same as the request I am submitting';

const DUPLICATE_LISTBOX_LABEL =
  'Open submissions near this location that may match your report. Select one or more to follow instead of submitting a new one.';

const DuplicateDetectionModal: React.FC<IDuplicateDetectionModalProps> = ({
  show,
  duplicates,
  onSubmitAnyway,
  onClose,
  onFollowDuplicate,
  onUnfollowDuplicate,
  onFollowAndSkip,
  onDiscard,
  submitAnywayLabel = 'Submit anyway',
  subtitle = DEFAULT_SUBTITLE,
  citizenDuplicateOverrideFlow = false,
}) => {
  const isStaffMode = !!onFollowDuplicate;
  const useCitizenOverrideFlow = !isStaffMode && citizenDuplicateOverrideFlow;
  const isCardSelectionEnabled = useCitizenOverrideFlow && !!onFollowAndSkip;

  const lockedIds = new Set<number>(
    useCitizenOverrideFlow
      ? duplicates.filter((d) => d.isFollowedByContact || d.isSubmittedByContact).map((d) => d.id)
      : []
  );

  const [noneOfTheseChecked, setNoneOfTheseChecked] = useState(false);
  const [selectedDuplicateIds, setSelectedDuplicateIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (show) {
      setNoneOfTheseChecked(false);
      setSelectedDuplicateIds(new Set());
    }
  }, [show]);

  const handleCardSelect = (duplicateId: number) => {
    setSelectedDuplicateIds((prev) => {
      const next = new Set(prev);
      if (next.has(duplicateId)) {
        next.delete(duplicateId);
      } else {
        next.add(duplicateId);
      }

      return next;
    });
    setNoneOfTheseChecked(false);
  };

  const handleNoneOfTheseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNoneOfTheseChecked(e.currentTarget.checked);
    if (e.currentTarget.checked) {
      setSelectedDuplicateIds(new Set());
    }
  };

  const count = duplicates.length;
  const title = `We found ${count} open ${count === 1 ? 'submission' : 'submissions'} near this location`;

  const duplicateRows = (itemRole: 'listitem' | 'option') =>
    duplicates.map((duplicate) => {
      const isLocked = lockedIds.has(duplicate.id);
      const isSelected = selectedDuplicateIds.has(duplicate.id);

      return (
        <DuplicateCard
          key={duplicate.id}
          duplicate={duplicate}
          isStaffMode={isStaffMode}
          isSelectable={isCardSelectionEnabled}
          isLocked={isCardSelectionEnabled ? isLocked : false}
          isSelected={isSelected}
          itemRole={itemRole}
          onSelect={isCardSelectionEnabled ? handleCardSelect : undefined}
          onFollow={onFollowDuplicate}
          onUnfollow={onUnfollowDuplicate}
          onCitizenUnfollow={isCardSelectionEnabled && onUnfollowDuplicate ? onUnfollowDuplicate : undefined}
        />
      );
    });

  const renderNoneOfTheseInteractiveRow = (asListItem: boolean) => (
    <div
      className="duplicate-card duplicate-card--none-of-these duplicate-card--none-of-these-override"
      {...(asListItem ? { role: 'listitem' as const } : {})}
    >
      <div className="duplicate-card__content">
        <label htmlFor={NONE_OF_THESE_CHECKBOX_ID} className="duplicate-card__none-of-these-label">
          <span className="duplicate-card__category">{NONE_OF_THESE_HEADING}</span>
        </label>
      </div>
      <div className="duplicate-card__checkbox duplicate-card__checkbox--interactive">
        <Checkbox
          id={NONE_OF_THESE_CHECKBOX_ID}
          name="duplicate-none-of-these"
          isChecked={noneOfTheseChecked}
          onChange={handleNoneOfTheseChange}
          htmlFor={false}
          className="duplicate-card__none-of-these-checkbox"
        >
          <span className="sr-only">{NONE_OF_THESE_HEADING}</span>
        </Checkbox>
      </div>
    </div>
  );

  const noneOfTheseDecorativeRow = (
    <div className="duplicate-card duplicate-card--none-of-these" role="listitem">
      <div className="duplicate-card__content">
        <span className="duplicate-card__category">{NONE_OF_THESE_HEADING}</span>
      </div>
      <div className="duplicate-card__checkbox" aria-hidden="true">
        <div className="duplicate-card__checkbox-box" />
      </div>
    </div>
  );

  const bodyElement = (
    <div>
      <p className="duplicate-detection-modal__subtitle">{subtitle}</p>
      {isCardSelectionEnabled ? (
        <div className="duplicate-detection-modal__list">
          <div
            className="duplicate-detection-modal__listbox"
            role="listbox"
            aria-label={DUPLICATE_LISTBOX_LABEL}
            aria-multiselectable="true"
          >
            {duplicateRows('option')}
          </div>
          {renderNoneOfTheseInteractiveRow(false)}
        </div>
      ) : (
        <div className="duplicate-detection-modal__list" role="list">
          {duplicateRows('listitem')}
          {!isStaffMode && (useCitizenOverrideFlow ? renderNoneOfTheseInteractiveRow(true) : noneOfTheseDecorativeRow)}
        </div>
      )}
    </div>
  );

  let footerElement: React.ReactNode = null;
  if (isStaffMode) {
    footerElement = (
      <div className="duplicate-detection-modal__footer-buttons">
        <Button
          bsStyle={ButtonStyle.DEFAULT}
          className="duplicate-detection-modal__discard-btn"
          content="Discard Submission"
          onClick={onDiscard}
        />
        <Button bsStyle={ButtonStyle.PRIMARY} content={submitAnywayLabel} onClick={onSubmitAnyway} />
      </div>
    );
  } else if (isCardSelectionEnabled) {
    if (noneOfTheseChecked) {
      footerElement = (
        <Button bsStyle={ButtonStyle.PRIMARY} content={SUBMIT_AFTER_NONE_OF_THESE_LABEL} onClick={onSubmitAnyway} />
      );
    } else if (selectedDuplicateIds.size > 0) {
      footerElement = (
        <Button
          bsStyle={ButtonStyle.PRIMARY}
          content={getFollowAndSkipLabel(selectedDuplicateIds.size)}
          onClick={() => onFollowAndSkip!(Array.from(selectedDuplicateIds))}
        />
      );
    }
  } else if (!useCitizenOverrideFlow || noneOfTheseChecked) {
    const footerButtonLabel = useCitizenOverrideFlow ? SUBMIT_AFTER_NONE_OF_THESE_LABEL : submitAnywayLabel;
    footerElement = <Button bsStyle={ButtonStyle.PRIMARY} content={footerButtonLabel} onClick={onSubmitAnyway} />;
  }

  return (
    <ModalBase
      show={show}
      onClose={onClose}
      closeButton
      closeOnEscape
      titleElement={title}
      bodyElement={bodyElement}
      footerElement={footerElement}
      showFooter
      className="duplicate-detection-modal__overlay"
      diaglogClassName="duplicate-detection-modal"
    />
  );
};

export default DuplicateDetectionModal;
